import { useState, memo, useEffect } from 'react';

import cn from 'classnames';

import useTheme from 'hooks/useTheme';
import i18n from 'i18n/config';
import Box from 'UIKit/Box';

import styles from './style.module.scss';
import TopCoinsOverview from './TopCoinsOverview';
import { MarketTopCoinsOverviewCardProps } from './types';

const title = i18n.t(
  'markets:topSectionCards.marketsOverview.marketsOverviewCards.topCoins.title'
);

const MarketTopCoinsOverviewCard = memo(
  ({ topCoinsData }: MarketTopCoinsOverviewCardProps) => {
    const { isLightTheme } = useTheme();

    const [showDefaultIcons, setShowDefaultIcons] = useState(
      Array(topCoinsData?.length).fill(false)
    );

    // The useEffect hook is used to reset the showDefaultIcons state whenever the data prop changes.
    // This ensures that the fallback images are properly managed whenever the component receives new data.

    useEffect(() => {
      setShowDefaultIcons(Array(topCoinsData?.length).fill(false));
    }, [topCoinsData]);

    const handleImageError = (index: number) => {
      setShowDefaultIcons(prevState => {
        const updatedIcons = [...prevState];
        updatedIcons[index] = true;
        return updatedIcons;
      });
    };

    const marketOverviewCardContainerClassNames = cn(styles.marketOverviewCardContainer, {
      [styles.lightTheme]: isLightTheme,
    });

    return (
      <Box
        className={marketOverviewCardContainerClassNames}
        data-testid="market-top-coins-overview-card"
      >
        <Box data-testid="topCoinsTitle" className={styles.title}>
          {title}
        </Box>
        <Box className={styles.marketOveriviewContainer}>
          <table className={styles.tableContainer}>
            {topCoinsData.map((symbol, index) => (
              <TopCoinsOverview
                key={symbol}
                symbol={symbol}
                index={index}
                showDefaultIcons={showDefaultIcons}
                handleImageError={handleImageError}
              />
            ))}
          </table>
        </Box>
      </Box>
    );
  }
);

MarketTopCoinsOverviewCard.displayName = 'MarketTopCoinsOverviewCard';

export default MarketTopCoinsOverviewCard;
